import * as THREE from 'three';
import { cellCenter, hexToWorld, T_INTERIOR, T_PLAZA, type Hex } from '@somnhold/sim';
import { WORLD_PER_HEX } from '../engine/coords.ts';
import { walledDist } from '../engine/wallgraph.ts';
import { GlowLayer } from '../engine/glow.ts';
import { faceGeo, pumpkinGeo } from '../models/seasonal.ts';
import { mulberry32, type Rng } from '../rand.ts';
import { dressHalloween } from './halloween-dress.ts';
import { Kit, place } from './kit.ts';
import type { RingPiece, SeasonTheme, ThemeCtx, ThemeScene } from './types.ts';

/*
 * The Night of Nightmares: the board under moonlight. Cold blue-violet night with orange
 * candle-light everywhere: jack-o'-lanterns along the wall-walks, around the keep and down every road,
 * the keep's windows lit, graveyards of crooked stones with dead trees, scarecrows in the fields,
 * bats wheeling overhead, will-o'-wisps in the woods and now and then a far flash of lightning.
 *
 * Cost: every static prop is merged into two meshes (painted and self-lit), every flame and window is a
 * glow sprite in one instanced layer, bats are one instanced draw, cobwebs one more. No extra lights.
 */

const K = WORLD_PER_HEX / 1000;
const TAU = Math.PI * 2;

const cellXZ = (h: Hex): THREE.Vector2 => {
  const c = hexToWorld(h.q, h.r);
  return new THREE.Vector2(c.x * K, c.y * K);
};

const hash = (n: number): number => {
  let h = Math.imul(n ^ 0x5bd1e995, 0x27d4eb2d);
  h ^= h >>> 15;
  return ((Math.imul(h, 0x2c1b3c6d) >>> 0) % 10007) / 10007;
};

/* ---------- prop geometry (built once, shared by every view) ---------- */

let PROTO: ReturnType<typeof makeProto> | null = null;

const branchGeo = (a: THREE.Vector3, b: THREE.Vector3, r1: number, r2: number): THREE.BufferGeometry => {
  const v = b.clone().sub(a);
  const g = new THREE.CylinderGeometry(r2, r1, v.length(), 5, 1);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.clone().normalize());
  g.applyMatrix4(new THREE.Matrix4().compose(a.clone().addScaledVector(v, 0.5), q, new THREE.Vector3(1, 1, 1)));
  return g.toNonIndexed();
};

/** A leafless tree: a leaning trunk forking into crooked, clawing branches (unit height ~1.6). */
const deadTree = (rng: Rng): THREE.BufferGeometry => {
  const parts: THREE.BufferGeometry[] = [];
  const grow = (a: THREE.Vector3, dir: THREE.Vector3, len: number, r: number, depth: number): void => {
    const b = a.clone().addScaledVector(dir, len);
    // A knee halfway: branches never run straight.
    const mid = a.clone().lerp(b, 0.5).add(new THREE.Vector3((rng() - 0.5) * len * 0.3, 0, (rng() - 0.5) * len * 0.3));
    parts.push(branchGeo(a, mid, r, r * 0.8), branchGeo(mid, b, r * 0.8, r * 0.55));
    if (depth <= 0) return;
    const n = depth >= 2 ? 3 : 2;
    for (let i = 0; i < n; i++) {
      const ang = rng() * TAU;
      const spread = 0.55 + rng() * 0.5;
      const nd = dir.clone().multiplyScalar(1 - spread * 0.6).add(new THREE.Vector3(Math.cos(ang) * spread, 0.15 + rng() * 0.25, Math.sin(ang) * spread)).normalize();
      grow(b, nd, len * (0.55 + rng() * 0.2), r * 0.55, depth - 1);
    }
  };
  grow(new THREE.Vector3(0, -0.05, 0), new THREE.Vector3((rng() - 0.5) * 0.3, 1, (rng() - 0.5) * 0.3).normalize(), 0.75, 0.075, 2);
  // Roots clutching the ground.
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + rng();
    parts.push(branchGeo(new THREE.Vector3(0, 0.12, 0), new THREE.Vector3(Math.cos(a) * 0.28, -0.02, Math.sin(a) * 0.28), 0.05, 0.015));
  }
  const g = mergeAll(parts);
  return g;
};

const mergeAll = (parts: THREE.BufferGeometry[]): THREE.BufferGeometry => {
  // Small local merge (positions and normals only) so a prototype is a single geometry.
  const flat = parts.map((p) => {
    const g = p.index ? p.toNonIndexed() : p;
    if (!g.getAttribute('normal')) g.computeVertexNormals();
    return g;
  });
  let n = 0;
  for (const g of flat) n += g.getAttribute('position').count;
  const pos = new Float32Array(n * 3);
  const nor = new Float32Array(n * 3);
  let o = 0;
  for (const g of flat) {
    pos.set(g.getAttribute('position').array as Float32Array, o * 3);
    nor.set(g.getAttribute('normal').array as Float32Array, o * 3);
    o += g.getAttribute('position').count;
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  return out;
};

const makeProto = () => {
  const rng = mulberry32(0x4a110);
  const arch = new THREE.Shape();
  arch.moveTo(-0.5, 0);
  arch.lineTo(-0.5, 0.75);
  arch.absarc(0, 0.75, 0.5, Math.PI, 0, true);
  arch.lineTo(0.5, 0);
  arch.closePath();
  const archGeo = new THREE.ExtrudeGeometry(arch, { depth: 0.16, bevelEnabled: false, curveSegments: 5 }).translate(0, 0, -0.08);
  const cross = mergeAll([new THREE.BoxGeometry(0.16, 1.3, 0.16).translate(0, 0.65, 0), new THREE.BoxGeometry(0.75, 0.16, 0.16).translate(0, 0.92, 0)]);
  const slab = new THREE.BoxGeometry(0.9, 0.9, 0.2).translate(0, 0.45, 0);
  const obelisk = mergeAll([new THREE.CylinderGeometry(0.16, 0.28, 1.4, 4).rotateY(Math.PI / 4).translate(0, 0.7, 0), new THREE.ConeGeometry(0.2, 0.3, 4).rotateY(Math.PI / 4).translate(0, 1.55, 0), new THREE.BoxGeometry(0.62, 0.18, 0.62).translate(0, 0.09, 0)]);
  // The bat: a top-down silhouette with scalloped wings (x = span, +z = forward).
  const bat = new THREE.Shape();
  bat.moveTo(0, -0.28);
  bat.lineTo(0.08, -0.12);
  bat.lineTo(0.5, -0.22);
  bat.quadraticCurveTo(0.4, -0.02, 0.44, 0.08);
  bat.quadraticCurveTo(0.3, 0.0, 0.26, 0.14);
  bat.quadraticCurveTo(0.16, 0.06, 0.07, 0.2);
  bat.lineTo(0, 0.34);
  bat.lineTo(-0.07, 0.2);
  bat.quadraticCurveTo(-0.16, 0.06, -0.26, 0.14);
  bat.quadraticCurveTo(-0.3, 0.0, -0.44, 0.08);
  bat.quadraticCurveTo(-0.4, -0.02, -0.5, -0.22);
  bat.lineTo(-0.08, -0.12);
  bat.closePath();
  return {
    pumpkin: pumpkinGeo(14, 9),
    faces: [faceGeo(0), faceGeo(1), faceGeo(2)],
    stem: new THREE.CylinderGeometry(0.06, 0.11, 0.34, 5),
    candle: new THREE.CylinderGeometry(0.03, 0.034, 1, 6).translate(0, 0.5, 0),
    flame: new THREE.ConeGeometry(0.024, 0.08, 5).translate(0, 0.04, 0),
    stones: [archGeo, cross, slab, obelisk],
    mound: new THREE.SphereGeometry(1, 7, 4, 0, TAU, 0, Math.PI / 2),
    trees: [deadTree(rng), deadTree(rng), deadTree(rng)],
    box: new THREE.BoxGeometry(1, 1, 1),
    cyl: new THREE.CylinderGeometry(0.5, 0.5, 1, 6),
    cone: new THREE.ConeGeometry(0.5, 1, 6),
    ball: new THREE.SphereGeometry(0.5, 7, 5),
    bat: new THREE.ShapeGeometry(bat, 2).rotateX(Math.PI / 2).scale(0.4, 0.4, 0.4),
  };
};

const proto = (): NonNullable<typeof PROTO> => (PROTO ??= makeProto());

/* ---------- procedural textures ---------- */


let webTex: THREE.Texture | null = null;
/** An orb-weaver's web: spokes and a spiral of sagging threads (white on transparent). */
const webTexture = (): THREE.Texture => {
  if (webTex) return webTex;
  const S = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d') as CanvasRenderingContext2D;
  const rng = mulberry32(0x3eb);
  const C = S / 2;
  const spokes = 11;
  const ang: number[] = [];
  for (let i = 0; i < spokes; i++) ang.push((i / spokes) * TAU + (rng() - 0.5) * 0.25);
  g.strokeStyle = 'rgba(255,255,255,0.95)';
  g.lineWidth = 1.4;
  for (const a of ang) {
    g.beginPath();
    g.moveTo(C, C);
    g.lineTo(C + Math.cos(a) * C, C + Math.sin(a) * C);
    g.stroke();
  }
  g.lineWidth = 1;
  g.strokeStyle = 'rgba(255,255,255,0.8)';
  for (let r = 7; r < C - 4; r += 5.5) {
    g.beginPath();
    for (let i = 0; i <= spokes; i++) {
      const a0 = ang[i % spokes] as number;
      const a1 = ang[(i + 1) % spokes] as number;
      const p0 = [C + Math.cos(a0) * r, C + Math.sin(a0) * r];
      const p1 = [C + Math.cos(a1 + (i === spokes - 1 ? TAU : 0)) * r, C + Math.sin(a1) * r];
      const mid = (a0 + a1 + (a1 < a0 ? TAU : 0)) / 2;
      if (i === 0) g.moveTo(p0[0] as number, p0[1] as number);
      g.quadraticCurveTo(C + Math.cos(mid) * r * 0.86, C + Math.sin(mid) * r * 0.86, p1[0] as number, p1[1] as number);
    }
    g.stroke();
  }
  webTex = new THREE.CanvasTexture(cv);
  webTex.colorSpace = THREE.SRGBColorSpace;
  return webTex;
};

/* ---------- the look ---------- */

const ORANGE = new THREE.Color('#ff8a1a');
const CANDLE = new THREE.Color('#ffb04a');
const WINDOW = new THREE.Color('#ff9a2a');
const WISP = new THREE.Color('#4dff8a');
const WISP_CORE = new THREE.Color('#d8ffe4');

interface Glow {
  p: THREE.Vector3;
  color: THREE.Color;
  size: number;
  op: number;
  /** Candle-like flicker (0 = steady). */
  flick: number;
  seed: number;
}

const BAT_VERT = /* glsl */ `
  attribute vec4 aOrbit;
  attribute vec3 aMove;
  uniform float uTime;
  varying float vShade;
  void main() {
    float a = aMove.y + uTime * aMove.x;
    vec3 c = vec3(aOrbit.x + cos(a) * aOrbit.z, aOrbit.w + sin(uTime * 0.9 + aMove.y * 3.0) * 0.35, aOrbit.y + sin(a) * aOrbit.z);
    vec3 fwd = normalize(vec3(-sin(a), 0.0, cos(a)) * sign(aMove.x));
    vec3 right = vec3(fwd.z, 0.0, -fwd.x);
    vec3 p = position;
    // Wing beats in stop-motion, like every miniature on the table.
    float tf = floor(uTime * 12.0) / 12.0;
    float flap = sin(tf * aMove.z + aMove.y * 7.0);
    p.y += abs(p.x) * flap * 0.85;
    vShade = 0.6 + 0.4 * flap;
    vec3 w = c + right * p.x + vec3(0.0, p.y, 0.0) + fwd * p.z;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }`;

const BAT_FRAG = /* glsl */ `
  uniform vec3 uColor;
  varying float vShade;
  void main() {
    gl_FragColor = vec4(uColor * vShade, 1.0);
    #include <colorspace_fragment>
  }`;

export const halloween: SeasonTheme = {
  id: 'halloween',
  look: {
    hemi: ['#6679b8', '#2a2638', 1.7],
    key: ['#b4c4ff', 2.3, [3, 20, -18]],
    rim: ['#8a6cf0', 1.0],
    brazier: 20,
    background: '#0a0911',
    fog: { color: '#0e0c17', near: 34, far: 74 },
    mist: { low: '#3e3a5e', mid: '#29383a', far: '#0b0a12', alpha: 1.15 },
  },
  dressEnemy: dressHalloween,
  build: (ctx) => buildHalloween(ctx),
};

const buildHalloween = (ctx: ThemeCtx): ThemeScene => {
  const P = proto();
  const low = ctx.low;
  const group = new THREE.Group();
  group.name = 'season:halloween';
  const rng = mulberry32(0x31 ^ (ctx.map.radius * 977));
  const glows: Glow[] = [];
  const ringGlows: Glow[] = [];
  const paint = new Kit();
  const lit = new Kit();
  /** Pumpkins have their own kit: the rind glows faintly warm, lit from within. */
  const rind = new Kit();
  const webs: { m: THREE.Matrix4 }[] = [];
  const paintMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0, flatShading: true });
  const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false });
  const rindMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0, flatShading: true, emissive: '#7a2a04', emissiveIntensity: 0.55 });
  const ground = (x: number, z: number): number => ctx.terrain.ground?.(x, z) ?? 0;
  const roadDist = (x: number, z: number): number => ctx.terrain.roadDist?.(x, z) ?? 99;
  const B = ctx.map.bastion;
  /** Hexes from the walled ground (B on the wall line): the ring and every district stay clear. */
  const hexDist = walledDist(ctx.map);

  /* --- prop builders (into the kits) --- */

  const orange = (): THREE.Color => new THREE.Color().setHSL(0.065 + rng() * 0.02, 0.78, 0.42 + rng() * 0.08);
  /** A jack-o'-lantern of radius r facing `yaw` (0 = +Z) at m. `carved` false: a plain pumpkin. */
  const lantern = (m: THREE.Matrix4, r: number, yaw: number, carved = true, list = glows, rindK = rind, litK = lit): void => {
    const local = place(0, r * 0.78, 0, yaw, r);
    rindK.add(P.pumpkin, orange(), m, local);
    rindK.add(P.stem, '#2e3a18', m, new THREE.Matrix4().multiplyMatrices(local, place(0.03, 0.86, 0, 0, 1, 0, -0.35)));
    if (!carved) return;
    litK.add(P.faces[Math.floor(rng() * 3)] as THREE.BufferGeometry, '#ffc25a', m, local);
    const p = new THREE.Vector3(Math.sin(yaw) * r * 1.1, r * 0.75, Math.cos(yaw) * r * 1.1).applyMatrix4(m);
    const s = new THREE.Vector3().setFromMatrixScale(m).x;
    list.push({ p, color: ORANGE, size: r * s * 3.6, op: 0.34, flick: 1, seed: rng() * 1000 });
  };
  const candle = (m: THREE.Matrix4, h: number, list = glows, paintK: Kit = paint, litK: Kit = lit): void => {
    paintK.add(P.candle, '#ddd3bb', m, place(0, 0, 0, 0, new THREE.Vector3(1, h, 1)));
    litK.add(P.flame, '#ffd27a', m, place(0, h, 0));
    const p = new THREE.Vector3(0, h + 0.04, 0).applyMatrix4(m);
    const s = new THREE.Vector3().setFromMatrixScale(m).x;
    list.push({ p, color: CANDLE, size: 0.32 * s, op: 0.8, flick: 1, seed: rng() * 1000 });
  };
  const stone = (x: number, z: number, yaw: number, s: number): void => {
    const y = ground(x, z);
    const v = Math.floor(rng() * 4);
    const grey = new THREE.Color().setHSL(0.6 + rng() * 0.1, 0.05, 0.3 + rng() * 0.12);
    const tilt = (rng() - 0.5) * 0.45;
    paint.add(P.stones[v] as THREE.BufferGeometry, grey, place(x, y - 0.02, z, yaw, s * (v === 1 ? 0.75 : 1), tilt, (rng() - 0.5) * 0.3));
    // The grave in front: a low heap of dark earth.
    const fx = x + Math.sin(yaw) * s * 0.8;
    const fz = z + Math.cos(yaw) * s * 0.8;
    paint.add(P.mound, '#2b241e', place(fx, ground(fx, fz) - 0.01, fz, yaw, new THREE.Vector3(s * 0.4, s * 0.12, s * 0.75)));
  };
  const tree = (x: number, z: number, s: number): void => {
    const c = new THREE.Color().setHSL(0.07, 0.14, 0.08 + rng() * 0.05);
    paint.add(P.trees[Math.floor(rng() * 3)] as THREE.BufferGeometry, c, place(x, ground(x, z), z, rng() * TAU, s, (rng() - 0.5) * 0.15, (rng() - 0.5) * 0.15));
  };
  const raven = (x: number, y: number, z: number, yaw: number): void => {
    const m = place(x, y, z, yaw, 0.12);
    const ink = '#0d0c12';
    paint.add(P.ball, ink, m, place(0, 0.32, 0, 0, new THREE.Vector3(0.6, 0.62, 1.1), -0.4));
    paint.add(P.ball, ink, m, place(0, 0.62, 0.32, 0, 0.42));
    paint.add(P.cone, '#2a2622', m, place(0, 0.6, 0.6, 0, new THREE.Vector3(0.14, 0.36, 0.14), Math.PI / 2));
    paint.add(P.box, ink, m, place(0, 0.22, -0.55, 0, new THREE.Vector3(0.3, 0.06, 0.5), 0.4));
    for (const k of [-1, 1]) paint.add(P.box, '#3a2f22', m, place(k * 0.1, 0.06, 0.05, 0, new THREE.Vector3(0.04, 0.16, 0.04)));
  };
  const scarecrow = (x: number, z: number, yaw: number): void => {
    const y = ground(x, z);
    const m = place(x, y, z, yaw, 0.85);
    const wood = '#4a3522';
    paint.add(P.cyl, wood, m, place(0, 0.7, 0, 0, new THREE.Vector3(0.07, 1.4, 0.07), 0.04));
    paint.add(P.cyl, wood, m, place(0, 1.05, 0, 0, new THREE.Vector3(0.06, 1.0, 0.06), 0, Math.PI / 2 + 0.08));
    // A ragged coat, straw bursting from the cuffs and hem.
    paint.add(P.cone, '#3b3226', m, place(0, 0.82, 0, 0, new THREE.Vector3(0.42, 0.62, 0.26), Math.PI));
    paint.add(P.box, '#2f2a24', m, place(0, 1.02, 0, 0, new THREE.Vector3(0.62, 0.12, 0.16), 0, 0.08));
    for (const k of [-1, 1]) {
      paint.add(P.cone, '#b39552', m, place(k * 0.52, 1.02, 0, 0, new THREE.Vector3(0.1, 0.18, 0.1), 0, (k * Math.PI) / 2));
      paint.add(P.cone, '#a78a48', m, place(k * 0.1, 0.48, 0, 0, new THREE.Vector3(0.08, 0.2, 0.08), Math.PI, k * 0.3));
    }
    paint.add(P.cyl, '#2a2a20', m, place(0, 0.66, 0, 0, new THREE.Vector3(0.36, 0.05, 0.22)));
    // A carved pumpkin for a head, under a crumpled hat.
    lantern(new THREE.Matrix4().multiplyMatrices(m, place(0.3, 0, 0.22)), 0.15, 0.4, true);
    const head = new THREE.Matrix4().multiplyMatrices(m, place(0, 1.12, 0));
    lantern(head, 0.17, 0, true);
    paint.add(P.cyl, '#16131a', head, place(0, 0.3, 0, 0, new THREE.Vector3(0.52, 0.03, 0.52)));
    paint.add(P.cone, '#16131a', head, place(0.02, 0.48, -0.02, 0, new THREE.Vector3(0.26, 0.4, 0.26), -0.35, 0.2));
    if (rng() < 0.7) raven(-0.48 * Math.cos(yaw) + x, y + 1.0, z + 0.48 * Math.sin(yaw), yaw + 1.2);
  };
  const web = (x: number, y: number, z: number, yaw: number, s: number, tilt = 0): void => {
    webs.push({ m: place(x, y, z, yaw, s, tilt) });
  };

  /* --- placement bookkeeping: keep props apart and off the roads --- */

  const taken: { x: number; z: number; r: number }[] = [];
  const free = (x: number, z: number, r: number, minRoad: number): boolean => {
    if (roadDist(x, z) < minRoad) return false;
    for (const t of taken) if ((t.x - x) ** 2 + (t.z - z) ** 2 < (t.r + r) ** 2) return false;
    return true;
  };
  const take = (x: number, z: number, r: number): void => {
    taken.push({ x, z, r });
  };
  // How far the board reaches before the fog: the spawns.
  let reach = (ctx.map.radius - 2) * WORLD_PER_HEX;
  const paths: THREE.Vector2[][] = [];
  for (const sec of ctx.map.sectors) {
    const pts = [cellXZ(sec.gate), ...sec.path.slice().reverse().map(cellXZ)];
    paths.push(pts);
    const s = pts[pts.length - 1] as THREE.Vector2;
    reach = Math.max(reach, s.length());
  }
  const inner = (B + 1.6) * WORLD_PER_HEX;
  /** A random spot in the outskirts band [r0, r1] (world radius), free and off the roads. */
  const spot = (r0: number, r1: number, rad: number, minRoad: number, maxRoad = 99): THREE.Vector2 | null => {
    for (let k = 0; k < 40; k++) {
      const a = rng() * TAU;
      const r = r0 + rng() * (r1 - r0);
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      if (hexDist(x, z) < B + 1.6) continue;
      const pd = roadDist(x, z);
      if (pd > maxRoad || !free(x, z, rad, minRoad)) continue;
      return new THREE.Vector2(x, z);
    }
    return null;
  };

  /* --- the keep: lit windows, lanterns at its foot and on its walk, cobwebs at the turrets --- */

  if (ctx.keep) {
    const km = ctx.keep;
    const kp = new THREE.Vector3().setFromMatrixPosition(km);
    take(kp.x, kp.z, 1.0);
    const ks = new THREE.Vector3().setFromMatrixScale(km).x;
    const win = (x: number, y: number, z: number, yaw: number): void => {
      lit.add(P.box, '#ffb347', km, place(x, y, z, yaw, new THREE.Vector3(0.05, 0.15, 0.012)));
      glows.push({ p: new THREE.Vector3(x, y, z).applyMatrix4(km), color: WINDOW, size: 0.42 * ks, op: 0.6, flick: 0.35, seed: rng() * 1000 });
    };
    for (const k of [-1, 1]) for (const y of [1.05, 1.55]) {
      win(k * 0.2, y, 0.418, 0);
      win(k * 0.2, y, -0.418, 0);
      win(k * 0.418, y, 0, Math.PI / 2);
    }
    // Light spilling from under the door.
    glows.push({ p: new THREE.Vector3(0, 0.22, 0.47).applyMatrix4(km), color: WINDOW, size: 0.7 * ks, op: 0.5, flick: 0.3, seed: 7 });
    // Lanterns crowding its foot (never toward a cell anyone builds on: the keep's own plaza ring).
    const n = low ? 6 : 10;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + rng() * 0.3;
      const r = 0.72 + rng() * 0.12;
      const x = Math.sin(a) * r;
      const z = Math.cos(a) * r;
      lantern(new THREE.Matrix4().multiplyMatrices(km, place(x, 0, z)), 0.07 + rng() * 0.06, a + (rng() - 0.5) * 0.6, rng() < 0.75);
    }
    // The walk on top: a lantern by each corner turret, and the turrets' webs.
    for (const [x, z] of [[-0.26, -0.26], [0.26, -0.26], [0.26, 0.26], [-0.26, 0.26]] as const) {
      lantern(new THREE.Matrix4().multiplyMatrices(km, place(x, 1.94, z)), 0.07, Math.atan2(x, z), true);
      const wp = new THREE.Vector3(x * 1.42, 1.78, z * 1.42).applyMatrix4(km);
      web(wp.x, wp.y, wp.z, Math.atan2(x, z) + Math.PI / 4, 0.32 * ks, -0.5);
    }
  }

  /* --- the outskirts --- */

  const hasOut = !!ctx.terrain.ground;
  if (hasOut) {
    // Jack-o'-lanterns lining the verges of every road, from the gate out to where the fog takes it.
    const spacing = low ? 2.6 : 1.8;
    for (const pts of paths) {
      let acc = spacing * 0.5;
      let side = 1;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1] as THREE.Vector2;
        const b = pts[i] as THREE.Vector2;
        const seg = b.clone().sub(a);
        const len = seg.length();
        const dir = seg.clone().normalize();
        const nrm = new THREE.Vector2(-dir.y, dir.x);
        for (let d = acc; d < len; d += spacing) {
          const p = a.clone().addScaledVector(dir, d);
          if (hexDist(p.x, p.y) < B + 1.3 || p.length() > reach + 0.5) continue;
          side = -side;
          let x = p.x + nrm.x * side * 0.95;
          let z = p.y + nrm.y * side * 0.95;
          if (roadDist(x, z) < 0.8) {
            x = p.x + nrm.x * side * 1.2;
            z = p.y + nrm.y * side * 1.2;
          }
          if (!free(x, z, 0.25, 0.75)) continue;
          take(x, z, 0.25);
          const yaw = Math.atan2(p.x - x, p.y - z);
          lantern(place(x, ground(x, z), z), 0.1 + rng() * 0.05, yaw, true);
          if (rng() < 0.45) candle(place(x + nrm.x * side * 0.2, ground(x, z), z + nrm.y * side * 0.2 + 0.08, 0, 1), 0.08 + rng() * 0.08);
        }
        acc = ((acc - len) % spacing + spacing) % spacing;
      }
    }

    // Every gate is kept by a pair of grinning lanterns either side of the road, and a few candles.
    if (ctx.map.kind === 'bastion') for (const pts of paths) {
      const g = pts[0] as THREE.Vector2;
      const n = pts[1] as THREE.Vector2 | undefined;
      if (!n) continue;
      const out = n.clone().sub(g).normalize();
      const side = new THREE.Vector2(-out.y, out.x);
      for (const s of [-1, 1]) {
        const x = g.x + out.x * 0.78 + side.x * s * 0.62;
        const z = g.y + out.y * 0.78 + side.y * s * 0.62;
        take(x, z, 0.3);
        lantern(place(x, ground(x, z), z), 0.15, Math.atan2(out.x, out.y) - s * 0.35, true);
        lantern(place(x + side.x * s * 0.2 + out.x * 0.12, ground(x, z), z + side.y * s * 0.2 + out.y * 0.12), 0.08, Math.atan2(out.x, out.y), rng() < 0.5);
        for (let k = 0; k < 3; k++) candle(place(x - out.x * 0.18 + side.x * s * (0.08 + k * 0.07), ground(x, z), z - out.y * 0.18 + side.y * s * (0.08 + k * 0.07)), 0.05 + rng() * 0.06);
      }
    }

    // Graveyards: crooked stones in ragged rows, a dead tree at the back, candles and a raven or two.
    const yards = low ? 2 : 4;
    for (let k = 0; k < yards; k++) {
      const c = spot(inner + 1.5, Math.max(inner + 3, reach - 1.5), 1.6, 2.4, 6);
      if (!c) continue;
      take(c.x, c.y, 1.7);
      const face = Math.atan2(-c.x, -c.y) + (rng() - 0.5) * 0.6;
      const right = new THREE.Vector2(Math.cos(face), -Math.sin(face));
      const fwd = new THREE.Vector2(Math.sin(face), Math.cos(face));
      const rows = 2 + Math.floor(rng() * 2);
      const cols = low ? 3 : 4;
      for (let r = 0; r < rows; r++) for (let cc = 0; cc < cols; cc++) {
        if (rng() < 0.15) continue;
        const ox = (cc - (cols - 1) / 2) * 0.55 + (rng() - 0.5) * 0.15;
        const oz = (r - (rows - 1) / 2) * 0.62 + (rng() - 0.5) * 0.15;
        const x = c.x + right.x * ox + fwd.x * oz;
        const z = c.y + right.y * ox + fwd.y * oz;
        if (roadDist(x, z) < 1.2) continue;
        stone(x, z, face + (rng() - 0.5) * 0.5, 0.2 + rng() * 0.09);
        if (rng() < 0.3) candle(place(x + fwd.x * 0.2 + right.x * 0.12, ground(x, z), z + fwd.y * 0.2 + right.y * 0.12), 0.06 + rng() * 0.07);
        if (rng() < 0.12) raven(x, ground(x, z) + 0.28, z, face + 2.5);
      }
      const bx = c.x - fwd.x * (rows * 0.4 + 0.4);
      const bz = c.y - fwd.y * (rows * 0.4 + 0.4);
      tree(bx, bz, 1.0 + rng() * 0.3);
      web(bx + 0.15, ground(bx, bz) + 0.9, bz, face, 0.35, -0.6);
      // A carved lantern left on a grave.
      lantern(place(c.x + fwd.x * 0.9, ground(c.x + fwd.x * 0.9, c.y + fwd.y * 0.9), c.y + fwd.y * 0.9), 0.11, face, true);
    }

    // Dead trees scattered over the heath.
    const trees = low ? 7 : 14;
    for (let i = 0; i < trees; i++) {
      const c = spot(inner + 0.6, reach + 1.5, 0.7, 2.0);
      if (!c) continue;
      take(c.x, c.y, 0.7);
      tree(c.x, c.y, 0.8 + rng() * 0.6);
      if (rng() < 0.3) raven(c.x + 0.1, ground(c.x, c.y) + 1.05, c.y, rng() * TAU);
    }

    // Scarecrows over the fields beside the roads.
    const crows = low ? 2 : 4;
    for (let i = 0; i < crows; i++) {
      const c = spot(inner + 0.8, inner + 6, 0.6, 1.5, 2.8);
      if (!c) continue;
      take(c.x, c.y, 0.6);
      scarecrow(c.x, c.y, Math.atan2(-c.x, -c.y) + (rng() - 0.5) * 0.8);
    }

    // Pumpkin patches: a tangle of vines with a crop of plain pumpkins and one carved.
    const patches = low ? 1 : 3;
    for (let i = 0; i < patches; i++) {
      const c = spot(inner + 0.8, reach - 1, 1.0, 1.6, 4);
      if (!c) continue;
      take(c.x, c.y, 1.0);
      const n = 6 + Math.floor(rng() * 4);
      for (let j = 0; j < n; j++) {
        const a = rng() * TAU;
        const r = Math.sqrt(rng()) * 0.85;
        const x = c.x + Math.cos(a) * r;
        const z = c.y + Math.sin(a) * r;
        lantern(place(x, ground(x, z) - 0.02, z), 0.07 + rng() * 0.07, Math.atan2(-x, -z), j === 0 || rng() < 0.15);
        paint.add(P.cyl, '#2c3a1e', place(x, ground(x, z) + 0.01, z, rng() * TAU, new THREE.Vector3(0.025, 0.6, 0.025), Math.PI / 2));
      }
    }
  }

  /* --- fallen leaves blown in over the flagstones (one instanced draw) --- */

  {
    const R = ctx.map.radius;
    const t = ctx.terrain.terrain;
    const leafM: THREE.Matrix4[] = [];
    const leafC: THREE.Color[] = [];
    const tones = ['#9a4416', '#7d3312', '#b0601c', '#6a2c14', '#8a6a24', '#5a2a18'];
    for (let id = 0; id < t.length; id++) {
      if (t[id] !== T_INTERIOR && t[id] !== T_PLAZA) continue;
      const n = rng() < (low ? 0.35 : 0.6) ? 1 + Math.floor(rng() * (low ? 2 : 4)) : 0;
      if (n === 0) continue;
      const c = cellCenter(R, id);
      const h = ctx.terrain.heights[id] ?? 0;
      for (let k = 0; k < n; k++) {
        const x = c.x * K + (rng() - 0.5) * WORLD_PER_HEX * 0.8;
        const z = c.y * K + (rng() - 0.5) * WORLD_PER_HEX * 0.8;
        leafM.push(place(x, h + 0.006 + k * 0.002, z, rng() * TAU, 0.07 + rng() * 0.05, (rng() - 0.5) * 0.2));
        leafC.push(new THREE.Color(tones[Math.floor(rng() * tones.length)] as string));
      }
    }
    if (leafM.length > 0) {
      const ls = new THREE.Shape();
      ls.moveTo(0, -0.5);
      ls.quadraticCurveTo(0.42, -0.1, 0.12, 0.2);
      ls.lineTo(0.3, 0.32);
      ls.lineTo(0, 0.5);
      ls.lineTo(-0.3, 0.32);
      ls.lineTo(-0.12, 0.2);
      ls.quadraticCurveTo(-0.42, -0.1, 0, -0.5);
      const im = new THREE.InstancedMesh(new THREE.ShapeGeometry(ls, 2).rotateX(-Math.PI / 2), new THREE.MeshStandardMaterial({ roughness: 1, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 }), leafM.length);
      leafM.forEach((m, i) => {
        im.setMatrixAt(i, m);
        im.setColorAt(i, leafC[i] as THREE.Color);
      });
      im.instanceMatrix.needsUpdate = true;
      im.computeBoundingSphere();
      im.name = 'halloween-leaves';
      group.add(im);
    }
  }

  /* --- glow sprites for every flame, window and wisp: one draw --- */

  const glowLayer = new GlowLayer(1024, THREE.AdditiveBlending, 0.7);
  glowLayer.mesh.renderOrder = 11;
  group.add(glowLayer.mesh);
  const wisps: { x: number; z: number; y: number; ph: number; r: number }[] = [];
  if (hasOut) {
    const n = low ? 4 : 9;
    for (let i = 0; i < n; i++) {
      const c = spot(Math.max(inner + 2, reach - 4), reach + 1.5, 0.3, 2.2);
      if (!c) continue;
      wisps.push({ x: c.x, z: c.y, y: ground(c.x, c.y) + 0.5 + rng() * 0.6, ph: rng() * TAU, r: 0.3 + rng() * 0.5 });
    }
  }

  /* --- meshes --- */

  for (const m of [paint.build(paintMat, 'halloween-props'), rind.build(rindMat, 'halloween-pumpkins'), lit.build(litMat, 'halloween-lit')]) if (m) group.add(m);
  const webMat = new THREE.MeshBasicMaterial({ map: webTexture(), color: '#cfd6e6', transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
  let webMesh: THREE.InstancedMesh | null = null;
  const ringWebs: THREE.Matrix4[] = [];
  const quad = new THREE.PlaneGeometry(1, 1);
  const rebuildWebs = (): void => {
    if (webMesh) {
      group.remove(webMesh);
      webMesh.dispose();
    }
    const all = [...webs.map((w) => w.m), ...ringWebs];
    if (all.length === 0 || low) return;
    webMesh = new THREE.InstancedMesh(quad, webMat, all.length);
    all.forEach((m, i) => webMesh!.setMatrixAt(i, m));
    webMesh.instanceMatrix.needsUpdate = true;
    webMesh.computeBoundingSphere();
    webMesh.renderOrder = 3;
    group.add(webMesh);
  };
  rebuildWebs();

  // Bats wheeling over the bastion; a few circle the keep's top.
  const batN = low ? 7 : 16;
  const bg = new THREE.InstancedBufferGeometry();
  bg.index = P.bat.index;
  bg.setAttribute('position', P.bat.getAttribute('position'));
  const orbit = new Float32Array(batN * 4);
  const move = new Float32Array(batN * 3);
  for (let i = 0; i < batN; i++) {
    const nearKeep = i < 4;
    const cr = nearKeep ? 0 : rng() * 4;
    const ca = rng() * TAU;
    orbit.set([Math.cos(ca) * cr, Math.sin(ca) * cr, nearKeep ? 1.1 + rng() * 1.2 : 3 + rng() * 9, nearKeep ? 2.5 + rng() * 0.9 : 2.6 + rng() * 2.2], i * 4);
    move.set([(rng() < 0.5 ? -1 : 1) * (nearKeep ? 0.7 + rng() * 0.4 : 0.18 + rng() * 0.22), rng() * TAU, 22 + rng() * 10], i * 3);
  }
  bg.setAttribute('aOrbit', new THREE.InstancedBufferAttribute(orbit, 4));
  bg.setAttribute('aMove', new THREE.InstancedBufferAttribute(move, 3));
  bg.instanceCount = batN;
  const batMat = new THREE.ShaderMaterial({
    vertexShader: BAT_VERT,
    fragmentShader: BAT_FRAG,
    uniforms: { uTime: { value: 0 }, uColor: { value: new THREE.Color('#0c0912') } },
    side: THREE.DoubleSide,
  });
  const bats = new THREE.Mesh(bg, batMat);
  bats.frustumCulled = false;
  group.add(bats);


  /* --- the ring (rebuilt when the walls break or mend) --- */

  let ringMeshes: THREE.Mesh[] = [];
  const ring = (pieces: RingPiece[]): void => {
    for (const m of ringMeshes) {
      group.remove(m);
      m.geometry.dispose();
    }
    ringGlows.length = 0;
    ringWebs.length = 0;
    const pk = new Kit();
    const rk = new Kit();
    const lk = new Kit();
    pieces.forEach((pc, i) => {
      const [kind, sized, wear] = pc.key.split(':');
      // The era IV and V rings name their look after the size ('smallVigil', 'fullTitan').
      const size = sized?.replace(/(Vigil|Titan)$/, '');
      if (wear === '2') return;
      const h = hash(i * 7 + 3);
      if (kind === 'rampartTower' && size === 'small') {
        // A lantern on the buttress top, grinning in at the keep.
        lantern(new THREE.Matrix4().multiplyMatrices(pc.m, place(0, 0.84, 0)), 0.12, Math.PI + (h - 0.5) * 0.8, true, ringGlows, rk, lk);
      } else if (kind === 'rampartTower') {
        for (const s of [-1, 1]) {
          const a = Math.PI + s * 0.32;
          lantern(new THREE.Matrix4().multiplyMatrices(pc.m, place(Math.sin(a) * 0.4, 1.12, Math.cos(a) * 0.4)), 0.075, a, true, ringGlows, rk, lk);
        }
        // Webs strung between the roof and the crenels.
        for (const s of [-1, 1]) ringWebs.push(new THREE.Matrix4().multiplyMatrices(pc.m, place(s * 0.34, 1.3, 0.05, s * Math.PI / 2, 0.36, -0.55)));
      } else if (kind === 'curtain') {
        const L = size === 'half' ? WORLD_PER_HEX / 2 : WORLD_PER_HEX;
        if (size !== 'half' && h < (low ? 0.6 : 0.75)) {
          const x = (h - 0.375) * L * 0.9;
          lantern(new THREE.Matrix4().multiplyMatrices(pc.m, place(x, 0.685, -0.09)), 0.085, Math.PI + (hash(i) - 0.5) * 0.6, true, ringGlows, rk, lk);
        }
        {
          const n = low ? (size === 'half' ? 0 : 1) : size === 'half' ? 1 : 2;
          for (let k = 0; k < n; k++) {
            const x = (hash(i * 13 + k) - 0.5) * L * 0.85;
            candle(new THREE.Matrix4().multiplyMatrices(pc.m, place(x, 0.685, -0.16)), 0.05 + hash(i + k * 5) * 0.06, ringGlows, pk, lk);
          }
        }
      }
    });
    ringMeshes = [pk.build(paintMat, 'halloween-ring'), rk.build(rindMat, 'halloween-ring-pumpkins'), lk.build(litMat, 'halloween-ring-lit')].filter((m): m is THREE.Mesh => !!m);
    for (const m of ringMeshes) group.add(m);
    rebuildWebs();
  };

  /* --- autumn on the woods: the broadleaves turn, the pines go black --- */

  ctx.terrain.decor.traverse((o) => {
    const im = o as THREE.InstancedMesh;
    if (!im.isInstancedMesh || !im.instanceColor) return;
    const c = new THREE.Color();
    const leaves = ['#7a3412', '#8f4a16', '#5a2410', '#6b521c', '#3f2616', '#a3561a', '#4a2018'];
    // The woods come in slices (outskirts.ts): each piece keeps its index in the whole kind, so it keeps its colour.
    const ids = im.userData.ids as Int32Array | undefined;
    for (let i = 0; i < im.count; i++) {
      im.getColorAt(i, c);
      const k = ids ? (ids[i] as number) : i;
      if (im.name === 'blobs') c.set(leaves[Math.floor(hash(k * 3 + 1) * leaves.length)] as string).multiplyScalar(0.8 + hash(k) * 0.35);
      else if (im.name === 'cones') c.multiplyScalar(0.62).lerp(new THREE.Color('#141c22'), 0.35);
      else if (im.name === 'tufts') c.set('#8f7a48').multiplyScalar(0.8 + hash(k) * 0.4);
      else continue;
      im.setColorAt(i, c);
    }
    im.instanceColor.needsUpdate = true;
  });

  /* --- per frame --- */

  const base = { hemi: ctx.lights.hemi.intensity, key: ctx.lights.key.intensity };
  const bgCol = (ctx.scene.background as THREE.Color | null)?.clone() ?? new THREE.Color('#0a0911');
  const flashCol = new THREE.Color('#3b3f66');
  let nextBolt = 6 + rng() * 6;
  let boltAt = -10;
  const tmpC = new THREE.Color();
  const frame = (t: number): void => {
    const tick = Math.floor(t * 12);
    glowLayer.begin();
    for (const list of [glows, ringGlows]) for (const g of list) {
      const f = g.flick > 0 ? 1 - g.flick * 0.45 * hash(tick * 31 + Math.floor(g.seed)) : 1;
      glowLayer.add(g.p.x, g.p.y, g.p.z, g.color, g.size * (0.9 + 0.1 * f), g.op * f);
    }
    for (const w of wisps) {
      const a = t * 0.5 + w.ph;
      const x = w.x + Math.cos(a) * w.r;
      const z = w.z + Math.sin(a * 1.3) * w.r;
      const y = w.y + Math.sin(t * 1.7 + w.ph) * 0.15;
      const pulse = 0.65 + 0.35 * Math.sin(t * 3.1 + w.ph * 2);
      glowLayer.add(x, y, z, WISP, 0.75, 0.4 * pulse);
      glowLayer.add(x, y, z, WISP_CORE, 0.2, 0.85 * pulse);
    }
    glowLayer.end();
    (batMat.uniforms.uTime as { value: number }).value = t;
    // Far lightning: a double flicker, cold light over everything, then gone.
    if (t > nextBolt) {
      boltAt = t;
      nextBolt = t + 9 + rng() * 14;
    }
    const dt = t - boltAt;
    const f = dt < 0 ? 0 : dt < 0.07 ? 1 : dt < 0.14 ? 0.15 : dt < 0.22 ? 0.75 : Math.max(0, 1 - (dt - 0.22) / 0.3) * 0.4;
    ctx.lights.hemi.intensity = base.hemi * (1 + 1.4 * f);
    ctx.lights.key.intensity = base.key * (1 + 1.1 * f);
    if (ctx.scene.background instanceof THREE.Color) ctx.scene.background.copy(tmpC.copy(bgCol).lerp(flashCol, f * 0.6));
  };

  /* --- the cursed pumpkin's burst --- */

  const boom = (kind: string, p: THREE.Vector3, r: number): boolean => {
    if (kind !== 'pumpkin') return false;
    const up = (h: number): THREE.Vector3 => p.clone().setY(p.y + h);
    const v = ctx.vfx;
    ctx.flare(up(0.6), '#ffffff', 1.0, 3);
    ctx.flare(up(0.7), '#ff8a1a', r * 1.3, 8);
    ctx.flare(up(0.5), '#ff5a10', r * 0.8, 13);
    v.ring(p, r * 0.15, r, '#ff7a1a', 7, 1);
    v.ring(up(0.15), r * 0.1, r * 0.75, '#ffd27a', 5, 0.8);
    // Burning seeds flung out, rind shards tumbling down, a green curse-wisp escaping.
    v.burst(up(0.45), { n: low ? 14 : 26, color: '#ffb347', speed: 0.15, up: 0.12, size: 0.07, life: 12, gravity: 0.012 });
    v.burst(up(0.4), { n: low ? 10 : 18, color: '#ff6a10', speed: 0.11, up: 0.1, size: 0.12, life: 9, gravity: 0.006 });
    v.burst(up(0.4), { n: low ? 8 : 14, color: '#c2500f', speed: 0.13, up: 0.15, size: 0.16, life: 14, gravity: 0.02 });
    v.burst(up(0.5), { n: 4, color: '#9dffb0', speed: 0.015, up: 0.06, size: 0.22, life: 16, gravity: -0.003 });
    // A ring of smoke rolling outward.
    const n = low ? 8 : 14;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU;
      v.burst(new THREE.Vector3(p.x + Math.cos(a) * r * 0.55, p.y + 0.12, p.z + Math.sin(a) * r * 0.55), { n: 1, color: '#2b2230', speed: 0.02, up: 0.02, size: 0.5, life: 16, smoke: true, spread: 0.1 });
    }
    ctx.shake(0.16);
    return true;
  };

  return {
    group,
    ring,
    frame,
    boom,
    dispose: () => {
      ctx.lights.hemi.intensity = base.hemi;
      ctx.lights.key.intensity = base.key;
      group.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh && m.geometry !== quad && m.geometry !== P.bat) m.geometry.dispose();
      });
      for (const m of [paintMat, rindMat, litMat, webMat, batMat, glowLayer.material]) m.dispose();
    },
  };
};

